/**
 * Auth-agnostic JSON fetch helper shared by every SPA.
 *
 *   - prepends `baseUrl` unless `path` is an absolute `http` URL
 *   - injects `Authorization: Bearer <token>` when `token` is provided
 *   - JSON-encodes plain-object bodies and parses JSON responses
 *   - throws `ApiError` on non-2xx
 *
 * Each SPA supplies its own bearer: the admin `useApi()` sends the Google ID
 * token, the partner `useApi()` its session JWT, and the member portal its
 * portal session. The customer app sends only the enrollment's `signToken`,
 * on the e-sign call.
 */

export interface ApiFetchInit extends Omit<RequestInit, "body"> {
  body?: BodyInit | Record<string, unknown> | null;
}

export interface ApiFetchOptions {
  /** Base URL prepended unless `path` is an absolute `http` URL. Pass `import.meta.env.VITE_API_BASE_URL ?? ""`. */
  baseUrl: string;
  /** Bearer token to send as Authorization header. Omit / pass undefined for unauthenticated calls. */
  token?: string;
  /**
   * How to read a successful (2xx) response body. `"json"` (default) parses
   * JSON or text; `"blob"` returns the raw `Blob`, for authed binary downloads
   * such as a preview PDF, which a plain tab can't open because the endpoint
   * requires a Bearer header. Error responses are always read as JSON or text
   * so `ApiError.detail` is populated.
   */
  responseType?: "json" | "blob";
}

/** Thrown for any non-2xx response so callers can `try { … } catch (e) { … }`. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly detail: string,
    public readonly body: unknown,
  ) {
    super(`API ${status}: ${detail}`);
    this.name = "ApiError";
  }
}

/**
 * The remaining cooldown a 429 carries, or null. The server wraps a cooldown
 * rejection as `{"detail": {"message", "retryAfterSeconds"}}`, which lands in
 * `ApiError.body` (`ApiError.detail` is only its string summary).
 */
export function retryAfterSecondsOf(err: unknown): number | null {
  if (!(err instanceof ApiError) || typeof err.body !== "object" || err.body === null) return null;
  const detail: unknown = "detail" in err.body ? err.body.detail : null;
  if (typeof detail !== "object" || detail === null || !("retryAfterSeconds" in detail)) {
    return null;
  }
  return typeof detail.retryAfterSeconds === "number" ? detail.retryAfterSeconds : null;
}

export async function apiFetch<T = unknown>(
  path: string,
  init: ApiFetchInit = {},
  options: ApiFetchOptions,
): Promise<T> {
  const { body: rawBody, headers: rawHeaders, ...restInit } = init;
  const { baseUrl, token, responseType = "json" } = options;

  const headers = new Headers(rawHeaders);
  if (token) headers.set("Authorization", `Bearer ${token}`);

  let body: BodyInit | null;
  if (rawBody === undefined || rawBody === null) {
    body = null;
  } else if (
    typeof rawBody === "string" ||
    rawBody instanceof FormData ||
    rawBody instanceof Blob ||
    rawBody instanceof ArrayBuffer ||
    rawBody instanceof URLSearchParams ||
    rawBody instanceof ReadableStream
  ) {
    body = rawBody;
  } else {
    // Plain object: JSON-encode it.
    if (!headers.has("Content-Type")) headers.set("Content-Type", "application/json");
    body = JSON.stringify(rawBody);
  }

  const url = path.startsWith("http") ? path : `${baseUrl}${path}`;
  const fetchInit: RequestInit = { ...restInit, headers };
  if (body !== null) fetchInit.body = body;
  const res = await fetch(url, fetchInit);

  // On success with `responseType: "blob"`, return the raw bytes untouched,
  // since reading a PDF as text would corrupt it. Errors take the JSON/text
  // path below so `ApiError.detail` is populated.
  if (res.ok && responseType === "blob") {
    return (await res.blob()) as T;
  }

  const contentType = res.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json");
  const payload: unknown = isJson
    ? await res.json().catch(() => null)
    : await res.text().catch(() => null);

  if (!res.ok) {
    const detail =
      isJson && payload && typeof payload === "object" && "detail" in payload
        ? String(payload.detail)
        : res.statusText;
    throw new ApiError(res.status, detail, payload);
  }

  return payload as T;
}
